import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { SFGridContainer, SFGridItem } from "../../grid";
import { SFStack } from "../../grid";
import SFTypography from "../../typography";
import SFCheckbox from "../../checkbox";
import SFRating from "../../rating";
import SFShowMoreBtn from "../../link/showMoreBtn";
import getStyled from "../../../hooks/getStyled";
import { SHOW_MORE_COUNT } from "./filterGroup";

const { styled } = getStyled();

const SFRatingStyled = styled(SFRating)(({ theme, value }) => ({
  // I'm basically targeting the filled part of the half star
  // and overriding the width to be 38% instead of 50% (default)
  ...(value % 1 !== 0 && {
    [`& .MuiRating-decimal:nth-child(${Math.round(value)})`]: {
      "& span:first-child": {
        width: "38% !important",
      },
    },
  }),
}));

export const RatingFilterGroupOption = ({
  colName,
  label,
  value,
  count = 0,
  currentSelection,
  updateCurrentSelection,
  disabled = false,
}) => {
  const isChecked = currentSelection[colName].includes(value);
  const handleChange = (event) => {
    updateCurrentSelection(colName, value);
  };
  return (
    <SFCheckbox
      checked={isChecked}
      onChange={handleChange}
      disabled={disabled}
      label={
        <SFStack direction="row" alignItems="center" spacing={1}>
          <SFRatingStyled
            readOnly
            precision={0.5}
            value={parseFloat(value)}
            size="xsmall"
          />
          <SFTypography variant="tableList">{`${
            label ?? String(value)
          } (${count})`}</SFTypography>
        </SFStack>
      }
    />
  );
};

export const RATING_VALUES = [
  "4.5",
  "4",
  "3.5",
  "3",
  "2.5",
  "2",
  "1.5",
  "1",
  "0.5",
  "0",
];

const RATING_INTERVALS = {
  4.5: [4.5, Infinity],
  4: [4, Infinity],
  3.5: [3.5, Infinity],
  3: [3, Infinity],
  2.5: [2.5, Infinity],
  2: [2, Infinity],
  1.5: [1.5, Infinity],
  1: [1, Infinity],
  0.5: [0.5, Infinity],
  0: [0, Infinity],
};

const RatingFilterGroup = ({
  colName,
  counts = [],
  currentSelection,
  updateCurrentSelection,
  defaultSelectedValues = [],
  disableAll = false,
}) => {
  const { t } = useTranslation();

  const [visibleCount, setVisibleCount] = useState(SHOW_MORE_COUNT);

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + SHOW_MORE_COUNT);
  };

  const getRatingLabel = (rating) =>
    t("components.fullSearchDialog.ratingLabel", { value: rating });

  const ratingValues = [...RATING_VALUES];

  const ratingCounts = getRatingCounts(counts, ratingValues);

  return (
    <SFGridContainer direction="column" spacing={0}>
      {ratingValues.slice(0, visibleCount).map((value, index) => (
        <SFGridItem key={index}>
          <RatingFilterGroupOption
            colName={colName}
            label={getRatingLabel(value)}
            value={value}
            count={ratingCounts.find((x) => x.value === value)?.count}
            currentSelection={currentSelection}
            updateCurrentSelection={updateCurrentSelection}
            disabled={defaultSelectedValues.includes(value) || disableAll}
          />
        </SFGridItem>
      ))}
      {visibleCount < ratingValues.length && (
        <SFGridItem>
          <SFShowMoreBtn onClick={handleShowMore} />
        </SFGridItem>
      )}
    </SFGridContainer>
  );
};

export default RatingFilterGroup;

const getRatingCounts = (counts, ratingValues) => {
  // Initialize an object to hold the counts for each rating interval
  const ratingCounts = {};

  // Pre-fill the ratingCounts with the ratingValues keys set to 0
  ratingValues.forEach((ratingValue) => {
    ratingCounts[ratingValue] = 0;
  });

  // Loop through each count entry once and increment the appropriate rating count
  counts.forEach(({ value, count }) => {
    for (const ratingValue of ratingValues) {
      const [min, max] = RATING_INTERVALS[ratingValue];
      if (parseFloat(value) >= min && parseFloat(value) < max) {
        ratingCounts[ratingValue] += count;
      }
    }
  });

  // Map the ratingCounts to the desired output format
  return ratingValues.map((value) => ({
    value,
    count: ratingCounts[value],
  }));
};
